/**
 * Unified login page component for the consolidated /login/ SPA.
 *
 * Replaces per-portal LoginPage components with a single login entry
 * point. After authentication, routes users to the correct portal based
 * on: referralCode query param, returnUrl query param, single account
 * type auto-redirect, or multi-portal PortalPicker.
 *
 * Return URL validation prevents open redirect attacks (CWE-601) by
 * checking against an allowlist of known portal base paths.
 *
 * @module shared/auth/UnifiedLoginPage
 */

import React, { useState, useCallback, useMemo } from 'react';
import type { LoginResponse, MfaMethod, MultiPortalResponse } from './types';
import { login, AuthApiError } from './auth-api';
import { MfaChallenge } from './mfa/MfaChallenge';
import { PortalPicker, PORTAL_URL_MAP } from './PortalPicker';

/** Default fallback redirect for subscribers. */
const DEFAULT_REDIRECT = '/subscriber-portal/subscriber/dashboard';

/** Allowlist of valid portal base paths for returnUrl validation (CWE-601). */
const ALLOWED_RETURN_PREFIXES = [
  '/subscriber-portal/',
  '/broker-admin-portal/',
  '/carrier-admin-portal/',
  '/association-admin-portal/',
  '/app-admin-portal/',
];

/**
 * Validates a returnUrl against the portal allowlist.
 * Rejects protocol injection (://, //) and unknown prefixes.
 */
export function isValidReturnUrl(url: string): boolean {
  if (!url) return false;
  // Block all non-path URLs (data:, blob:, javascript:, etc.) in one check.
  if (!url.startsWith('/')) return false;
  if (url.includes('://')) return false;
  if (url.startsWith('//')) return false;
  return ALLOWED_RETURN_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/** Resolves the API base URL from the global config injected by config.js. */
function getApiBaseUrl(): string {
  return typeof window !== 'undefined'
    ? ((window as unknown as Record<string, unknown>).__IPA_API_BASE_URL__ as string) ?? ''
    : '';
}

/** Inline styles for the login page (mobile-first, no external CSS framework). */
const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '1rem',
    backgroundColor: '#f3f4f6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  } as React.CSSProperties,
  card: {
    width: '100%',
    maxWidth: '28rem',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
  } as React.CSSProperties,
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    textAlign: 'center' as const,
    margin: '0 0 1.5rem 0',
    color: '#111827',
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: '0.25rem',
    color: '#374151',
  } as React.CSSProperties,
  input: {
    display: 'block',
    width: '100%',
    minHeight: '48px',
    padding: '0.625rem 0.75rem',
    fontSize: '1rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    marginBottom: '1rem',
  } as React.CSSProperties,
  button: {
    display: 'block',
    width: '100%',
    minHeight: '48px',
    padding: '0.75rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  } as React.CSSProperties,
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  } as React.CSSProperties,
  error: {
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    color: '#991b1b',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
  } as React.CSSProperties,
} as const;

/** MFA challenge state stored when the Auth Service requires a second factor. */
interface MfaChallengeState {
  mfaSessionId: string;
  mfaMethods: MfaMethod[];
}

/**
 * Maps account types to their portal URL prefix for returnUrl validation.
 * Derived from PORTAL_URL_MAP to prevent the two maps from drifting out of sync.
 */
const ACCOUNT_TYPE_PORTAL_PREFIX: Record<string, string> = Object.fromEntries(
  Object.entries(PORTAL_URL_MAP).map(([type, url]) => {
    const secondSlash = url.indexOf('/', 1);
    return [type, url.slice(0, secondSlash + 1)];
  }),
);

/**
 * Checks whether a returnUrl belongs to a portal that the user has access to.
 * Prevents stale returnUrl values from a previous session redirecting users
 * to portals they did not just authenticate for.
 */
function isReturnUrlForAccountTypes(url: string, accountTypes: string[]): boolean {
  return accountTypes.some((type) => {
    const prefix = ACCOUNT_TYPE_PORTAL_PREFIX[type];
    return prefix && url.startsWith(prefix);
  });
}

/**
 * Determines the post-authentication redirect URL based on query params
 * and user account types.
 */
function resolveRedirectUrl(
  accountTypes: string[],
  searchParams: URLSearchParams,
): string | null {
  // Priority 1: referralCode → always subscriber portal
  if (searchParams.get('referralCode')) {
    return DEFAULT_REDIRECT;
  }

  // Priority 2: valid returnUrl — must pass allowlist AND match the user's account types.
  // A returnUrl from a previous session (e.g. /carrier-admin-portal/dashboard) must not
  // redirect a subscriber to the wrong portal.
  const returnUrl = searchParams.get('returnUrl');
  if (returnUrl && isValidReturnUrl(returnUrl) && isReturnUrlForAccountTypes(returnUrl, accountTypes)) {
    return returnUrl;
  }

  // Priority 3: single account type → auto-redirect
  if (accountTypes.length === 1) {
    return PORTAL_URL_MAP[accountTypes[0]] ?? DEFAULT_REDIRECT;
  }

  // Multi-portal: caller should show PortalPicker
  return null;
}

/**
 * Unified login page component for the consolidated /login/ SPA.
 *
 * Authenticates via the same multi-step flow as the per-portal LoginPage:
 * 1. POST /api/v1/auth/login with email/password
 * 2. If MFA required: display MFA challenge UI
 * 3. If multi-portal: show PortalPicker or auto-redirect
 * 4. GET /api/v1/auth/me to fetch user profile
 * 5. Store user data in sessionStorage
 * 6. Redirect based on referralCode, returnUrl, or account type
 */
export function UnifiedLoginPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeState | null>(null);
  const [multiPortal, setMultiPortal] = useState<{ availableTypes: string[]; selectionToken: string; expiresAt?: string } | null>(null);

  const searchParams = useMemo(
    () => typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams(),
    [],
  );

  /** Completes the login flow after credentials (and MFA if needed) are verified. */
  const completeLogin = useCallback(async (accessToken?: string) => {
    // Clear stale session data from any previous user before writing new data.
    // Prevents portal grants, user profile, and auth state from a prior session
    // leaking into the new session and causing wrong-dashboard redirects.
    sessionStorage.removeItem('ipa_auth_token');
    sessionStorage.removeItem('ipa_auth_user');
    sessionStorage.removeItem('ipa_auth_authenticated');
    sessionStorage.removeItem('ipa_portal_grants');

    if (accessToken) {
      sessionStorage.setItem('ipa_auth_token', accessToken);
    }

    // Mark as authenticated early so portal sessionStorage fallback works
    // even if the redirect races with the portal's initial /auth/me call.
    sessionStorage.setItem('ipa_auth_authenticated', 'true');

    // Fetch user profile using the cookie (production flow)
    try {
      const meResponse = await fetch(`${getApiBaseUrl()}/api/v1/auth/me`, {
        method: 'GET',
        headers: { 'X-Correlation-ID': crypto.randomUUID() },
        credentials: 'include',
      });

      if (!meResponse.ok) {
        sessionStorage.removeItem('ipa_auth_authenticated');
        setError('Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      const meData = (await meResponse.json()) as Record<string, unknown>;
      const rawAccountType = (meData.accountType as string) ?? '';

      const authUser = {
        id: meData.userId,
        email: meData.email,
        firstName: meData.firstName,
        lastName: meData.lastName,
        accountType: rawAccountType,
        role: rawAccountType,
        organizationId: meData.organizationId,
        permissions: meData.permissions ?? meData.roles ?? [],
        timezone: meData.timezone,
      };

      sessionStorage.setItem('ipa_auth_user', JSON.stringify(authUser));

      // Determine redirect based on account type and query params
      const accountTypes = meData.availableTypes as string[] | undefined;
      const typesForRedirect = accountTypes ?? (rawAccountType ? [rawAccountType] : []);
      const redirectUrl = resolveRedirectUrl(typesForRedirect, searchParams);

      window.location.href = redirectUrl ?? DEFAULT_REDIRECT;
    } catch {
      sessionStorage.removeItem('ipa_auth_authenticated');
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  }, [searchParams]);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response: LoginResponse = await login({ email, password });

      if ('multiPortal' in response && response.multiPortal) {
        const multiPortalResponse = response as MultiPortalResponse;

        // Check if we can auto-redirect (referralCode or valid returnUrl)
        const redirectUrl = resolveRedirectUrl(
          multiPortalResponse.availableTypes,
          searchParams,
        );

        if (redirectUrl && (searchParams.get('referralCode') || searchParams.get('returnUrl'))) {
          // Must still show PortalPicker to create the session — portal selection is
          // required before redirect. After selection, PortalPicker handles the redirect.
          setMultiPortal({
            availableTypes: multiPortalResponse.availableTypes,
            selectionToken: multiPortalResponse.selectionToken,
            expiresAt: multiPortalResponse.expiresAt,
          });
          setLoading(false);
          return;
        }

        setMultiPortal({
          availableTypes: multiPortalResponse.availableTypes,
          selectionToken: multiPortalResponse.selectionToken,
          expiresAt: multiPortalResponse.expiresAt,
        });
        setLoading(false);
        return;
      }

      if ('mfaRequired' in response && response.mfaRequired) {
        setMfaChallenge({
          mfaSessionId: response.mfaSessionId,
          mfaMethods: response.mfaMethods,
        });
        setLoading(false);
        return;
      }

      // No MFA required: complete login
      await completeLogin('accessToken' in response ? response.accessToken : undefined);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(err.message);
      } else {
        setError('Network error. Please check your connection and try again.');
      }
      setLoading(false);
    }
  }, [email, password, completeLogin, searchParams]);

  const handleMfaSuccess = useCallback((accessToken?: string) => {
    void completeLogin(accessToken);
  }, [completeLogin]);

  const handleMfaCancel = useCallback(() => {
    setMfaChallenge(null);
    setPassword('');
    setError('');
  }, []);

  const handleTokenExpired = useCallback(() => {
    setMultiPortal(null);
    setError('Session expired, please login again');
    setPassword('');
  }, []);

  // Show portal picker if the user has multiple account types
  if (multiPortal) {
    return (
      <PortalPicker
        availableTypes={multiPortal.availableTypes}
        selectionToken={multiPortal.selectionToken}
        onTokenExpired={handleTokenExpired}
        expiresAt={multiPortal.expiresAt}
      />
    );
  }

  // Show MFA challenge if the Auth Service requires a second factor
  if (mfaChallenge) {
    return (
      <div style={styles.wrapper} data-testid="login-page">
        <MfaChallenge
          mfaSessionId={mfaChallenge.mfaSessionId}
          mfaMethods={mfaChallenge.mfaMethods}
          onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
        />
      </div>
    );
  }

  return (
    <div style={styles.wrapper} data-testid="login-page">
      <style>{`
        @media (min-width: 768px) {
          [data-testid="login-page"] > [data-testid="login-card"] {
            max-width: 480px;
            padding: 2.5rem;
          }
        }
        @media (min-width: 1024px) {
          [data-testid="login-page"] > [data-testid="login-card"] {
            max-width: 560px;
            padding: 3rem 3.5rem;
          }
          [data-testid="login-page"] > [data-testid="login-card"] h1 {
            font-size: 2rem;
          }
        }
      `}</style>
      <div style={styles.card} data-testid="login-card">
        <h1 style={styles.title}>IPA Platform</h1>

        {error && (
          <div
            role="alert"
            style={styles.error}
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label htmlFor="login-email" style={styles.label}>
            Email
          </label>
          <input
            id="login-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            style={styles.input}
            data-testid="login-email"
          />

          <label htmlFor="login-password" style={styles.label}>
            Password
          </label>
          <input
            id="login-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            style={styles.input}
            data-testid="login-password"
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
